/**
 * ReferralLink component - displays and allows copying of the referral link
 * Also allows customizing the referral slug
 */
import React, { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CopyButton,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconEdit } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useReferralStore } from "@/stores/referralStore";
import { SocialShareButtons } from "./SocialShareButtons";
import { reloadExtensionOnboardingSteps } from "@utils/extensionClient";

// Color constants
const COLORS = {
  CARD_BACKGROUND: "#f2fbff",
  CARD_SHADOW: "rgba(0, 0, 0, 0.15)",
  LINK_BOX_BACKGROUND: "white",
  LINK_BOX_BACKGROUND_HOVER: "#005591",
  LINK_BOX_BORDER: "#D0E7FF",
  LINK_BOX_TEXT: "#0077CC",
  LINK_BOX_TEXT_HOVER: "white",
  LINK_BOX_SHADOW: "rgba(0, 119, 204, 0.1)",
  LINK_BOX_SHADOW_HOVER: "rgba(0, 119, 204, 0.2)",
};

// Layout constants
const LINK_BOX_MAX_WIDTH = 1000;

export const ReferralLink: React.FC = () => {
  const dashboard = useReferralStore((state) => state.dashboard);
  const isLoading = useReferralStore((state) => state.isLoadingDashboard);
  const {
    slugData,
    isLoadingSlug,
    isEditingSlug,
    customSlugInput,
    slugCheckMessage,
    isSlugAvailable,
    isCheckingSlug,
    isSavingSlug,
    setIsEditingSlug,
    setCustomSlugInput,
    checkSlugAvailabilityAsync,
    saveSlug,
    cancelEdit,
  } = useReferralStore();

  const referralLink = dashboard?.referralLink || "";

  // Hover state for background swap
  const [isHovering, setIsHovering] = React.useState(false);
  // Celebration state for click animation
  const [isCelebrating, setIsCelebrating] = React.useState(false);

  // Debounced slug availability check
  useEffect(() => {
    if (!isEditingSlug || !customSlugInput.trim()) {
      return;
    }

    // Skip check if it's the current slug
    if (customSlugInput === slugData?.referralSlug) {
      return;
    }

    const timer = setTimeout(() => {
      checkSlugAvailabilityAsync(customSlugInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [
    customSlugInput,
    isEditingSlug,
    slugData?.referralSlug,
    checkSlugAvailabilityAsync,
  ]);

  const isSaveDisabled = () => {
    return (
      isSavingSlug ||
      isCheckingSlug ||
      (customSlugInput.trim() && isSlugAvailable !== true) ||
      customSlugInput === slugData?.referralSlug
    );
  };

  const handleCopyClick = (copyFn: () => void) => {
    if (!referralLink) return;

    // Copy to clipboard
    copyFn();

    // Clear hover state and trigger celebration
    setIsHovering(false);
    setIsCelebrating(true);

    // Reset after animation completes
    setTimeout(() => {
      setIsCelebrating(false);
    }, 800);
  };

  const handleSave = async () => {
    if (isSaveDisabled()) {
      // Show notification explaining why save is disabled
      if (isCheckingSlug) {
        notifications.show({
          title: "Please wait",
          message: "Checking slug availability...",
          color: "blue",
        });
      } else if (customSlugInput.trim() && isSlugAvailable !== true) {
        notifications.show({
          title: "Slug not available",
          message: "Please choose a different slug or wait for validation",
          color: "orange",
          icon: <IconAlertCircle size={16} />,
        });
      } else if (customSlugInput === slugData?.referralSlug) {
        notifications.show({
          title: "No changes",
          message: "The slug is the same as your current one",
          color: "blue",
        });
      }
      return;
    }

    if (!customSlugInput.trim() && !slugData?.referralSlug) {
      // Nothing to save
      setIsEditingSlug(false);
      return;
    }

    try {
      const newSlug = customSlugInput.trim() || null;
      await saveSlug(newSlug);

      // Notify extension to reload onboarding steps after creating custom slug
      try {
        await reloadExtensionOnboardingSteps();
      } catch (error) {
        console.error("Failed to reload extension onboarding steps:", error);
      }

      notifications.show({
        title: "Success",
        message: "Custom referral link updated successfully",
        color: "green",
        icon: <IconCheck size={16} />,
      });
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message:
          err.response?.data?.message || "Failed to update referral link",
        color: "red",
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  return (
    <Stack
      gap="lg"
      style={{
        maxWidth: `${LINK_BOX_MAX_WIDTH}px`,
        width: "100%",
        margin: "0 auto",
      }}
    >
      {/* Gradient Card - Header, Description, and URL */}
      <Card
        p="lg"
        radius="md"
        style={{
          background: isCelebrating
            ? "white"
            : "linear-gradient(to left, #00aeef, #5c59e8)",
          transition: "background 0.2s ease-out",
          boxShadow: `0 4px 12px ${COLORS.CARD_SHADOW}`,
        }}
      >
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={8} style={{ flex: 1 }}>
              <Text
                fw={600}
                size="lg"
                c={isCelebrating ? "#0077CC" : "white"}
                style={{
                  transition: "color 0.2s ease-out",
                }}
              >
                Your Referral Link
              </Text>
              <Text
                size="sm"
                c={isCelebrating ? "#0077CC" : "white"}
                style={{
                  transition: "color 0.2s ease-out",
                  opacity: 0.95,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                }}
              >
                Your unique referral link that you can share with others. When
                someone signs up using this link, you receive a reward!
              </Text>
            </Stack>
            {!isEditingSlug && !isLoadingSlug && (
              <Button
                variant="white"
                leftSection={<IconEdit size={16} />}
                onClick={() => setIsEditingSlug(true)}
                style={{ flexShrink: 0 }}
              >
                Edit
              </Button>
            )}
          </Group>

          {/* URL Display/Edit Section */}
          {!isEditingSlug ? (
            <>
              {/* URL Badge - Clickable to copy */}
              <CopyButton value={referralLink || ""}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? "Copied!" : "Click to copy"}
                    position="top"
                  >
                    <Box
                      onClick={
                        referralLink ? () => handleCopyClick(copy) : undefined
                      }
                      onMouseEnter={() => {
                        if (referralLink && !isCelebrating) {
                          setIsHovering(true);
                        }
                      }}
                      onMouseLeave={() => {
                        if (referralLink && !isCelebrating) {
                          setIsHovering(false);
                        }
                      }}
                      style={{
                        padding: "16px 20px",
                        background: isCelebrating
                          ? "linear-gradient(to left, #00aeef, #5c59e8)"
                          : "white",
                        border: "none",
                        borderRadius: "8px",
                        fontFamily: "monospace",
                        fontSize: "16px",
                        fontWeight: 600,
                        color: isCelebrating ? "white" : COLORS.LINK_BOX_TEXT,
                        textAlign: "center",
                        cursor: referralLink ? "pointer" : "not-allowed",
                        transition: "all 0.2s ease-out",
                        boxShadow: isCelebrating
                          ? "0 8px 24px rgba(0, 119, 204, 0.5)"
                          : "0 2px 8px rgba(0, 0, 0, 0.1)",
                        opacity: referralLink ? 1 : 0.6,
                        width: "100%",
                        transform: isCelebrating
                          ? "scale(1.02) translateY(-4px)"
                          : isHovering
                            ? "scale(1.02) translateY(-2px)"
                            : "scale(1) translateY(0)",
                      }}
                    >
                      {isCelebrating ? (
                        <Group gap="xs" justify="center">
                          <IconCheck size={20} />
                          <span>Copied!</span>
                        </Group>
                      ) : (
                        referralLink || "Loading..."
                      )}
                    </Box>
                  </Tooltip>
                )}
              </CopyButton>

              {!referralLink && !isLoading && (
                <Text
                  size="sm"
                  c={isCelebrating ? "#0077CC" : "white"}
                  style={{
                    opacity: 0.9,
                    transition: "color 0.2s ease-out",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  Referral link is being generated. Please wait a moment and
                  refresh the page.
                </Text>
              )}
            </>
          ) : (
            <>
              {/* Inline URL Editor */}
              <Stack gap="xs">
                <Box
                  style={{
                    padding: "16px 20px",
                    backgroundColor: "white",
                    border: "none",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Group gap={0} wrap="nowrap" align="center" justify="center">
                    <Text
                      style={{
                        fontFamily: "monospace",
                        fontSize: "16px",
                        fontWeight: 600,
                        color: COLORS.LINK_BOX_TEXT,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {(() => {
                        if (!referralLink) return "";

                        // Extract the base URL by finding the last segment
                        // Referral links follow pattern: {baseUrl}/partners/{slug}/
                        // We want to show: {baseUrl}/partners/
                        const url = new URL(referralLink);
                        const pathSegments = url.pathname
                          .split("/")
                          .filter(Boolean);

                        // Remove the last segment (the slug or user ID)
                        if (pathSegments.length > 0) {
                          pathSegments.pop();
                        }

                        // Reconstruct base URL with trailing slash
                        return `${url.origin}/${pathSegments.join("/")}/`;
                      })()}
                    </Text>
                    <TextInput
                      value={customSlugInput}
                      onChange={(e) => {
                        // Only allow lowercase letters, numbers, and hyphens
                        // Replace spaces with dashes, then remove all invalid characters
                        const value = e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, "-")
                          .replace(/[^a-z0-9-]/g, "");
                        setCustomSlugInput(value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSave();
                        }
                      }}
                      placeholder="your-custom-name"
                      maxLength={30}
                      styles={{
                        input: {
                          fontFamily: "monospace",
                          fontSize: "16px",
                          fontWeight: 600,
                          color: COLORS.LINK_BOX_TEXT,
                          textAlign: "center",
                          border: "none",
                          borderBottom: `2px solid ${COLORS.LINK_BOX_TEXT}`,
                          borderRadius: 0,
                          padding: "4px 8px",
                          paddingRight: "36px", // Make room for the validation icon
                          minWidth: "200px",
                          backgroundColor: "transparent",
                        },
                      }}
                      rightSection={
                        isCheckingSlug ? (
                          <Loader size="xs" />
                        ) : isSlugAvailable !== null ? (
                          isSlugAvailable ? (
                            <IconCheck size={16} color="green" />
                          ) : (
                            <IconAlertCircle size={16} color="red" />
                          )
                        ) : null
                      }
                    />
                  </Group>
                </Box>

                {/* Validation message */}
                {slugCheckMessage && (
                  <Text
                    size="sm"
                    fw={500}
                    c={isSlugAvailable ? "green" : "red"}
                    ta="center"
                  >
                    {slugCheckMessage}
                  </Text>
                )}

                {/* Helper text */}
                <Text
                  size="xs"
                  c={isCelebrating ? "#0077CC" : "white"}
                  ta="center"
                  style={{
                    opacity: 0.9,
                    transition: "color 0.2s ease-out",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  3-30 characters: lowercase letters, numbers, and hyphens. Must
                  start with a letter.
                </Text>
              </Stack>

              {/* Action buttons below the URL */}
              <Group gap="xs" justify="center">
                <Button
                  size="sm"
                  color="red"
                  onClick={cancelEdit}
                  disabled={isSavingSlug}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  color="green"
                  onClick={handleSave}
                  disabled={isSaveDisabled()}
                  loading={isSavingSlug}
                >
                  Save Custom Link
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Card>

      {/* Social Share Section - Separate from gradient card */}
      {!isEditingSlug && (
        <Box style={{ display: "flex", justifyContent: "center" }}>
          <SocialShareButtons referralLink={referralLink} />
        </Box>
      )}
    </Stack>
  );
};
